import type { ConvertToRestDayVM } from '@client/new/pages/workout/components/useConvertToRestDay';
import ajax from '../../../../components/ajax';
import { useConvertToRestDay } from '@client/new/pages/workout/components/useConvertToRestDay';
import {
  DuplicateDialogProps,
  useDuplicateWorkout,
} from '@client/new/features/workout-duplicate';
import { useGetScheduledWorkoutsQuery } from '../../../../../generated/graphql';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { toLiteWorkout } from '@client/new/pages/collection/workouts/workouts.hooks';
import { LiteWorkout } from '@client/new/pages/collection/workouts/workouts.domain';
import {
  WorkoutsSearchVM,
  buildWorkoutsListTable,
  filterWorkoutsByTitle,
  toProgramNameById,
} from './workoutTableExtras';
import {
  WorkoutsPagination,
  useWorkoutsPageData,
  useWorkoutsSearch,
} from './workoutsList.hooks';

type ScheduledWorkout = {
  id: number;
  name?: string | null;
  publishedAt?: string | null;
  programIndex?: number | null;
};

const toProgramOrderKey = (w: ScheduledWorkout) => {
  // Mirrors OrderedProgram.workoutsOrder: day is in 1-7 range, week 0 = no
  // week/day prefix in the title (intro/overview workouts sort first).
  const week = w.name?.match(/^[Ww](\d+)/);
  const day = w.name?.match(/^[Ww]\d+\s*\/\s*[Dd](\d+)/);
  return (
    (week ? parseInt(week[1], 10) : 0) * 10 + (day ? parseInt(day[1], 10) : 0)
  );
};

const byProgramOrder = (a: ScheduledWorkout, b: ScheduledWorkout) => {
  if (
    a.programIndex != null &&
    b.programIndex != null &&
    a.programIndex !== b.programIndex
  ) {
    return a.programIndex - b.programIndex;
  }

  const orderDiff = toProgramOrderKey(a) - toProgramOrderKey(b);
  return orderDiff !== 0 ? orderDiff : a.id - b.id;
};

// The schedule modal historically saved publishedAt with wall-clock seconds,
// so batch-scheduled workouts differ at second precision while the UI only
// shows minutes. Compare at the displayed granularity and let program order
// decide within a minute.
const toScheduledMinute = (date: string) =>
  Math.floor(Date.parse(date) / 60_000);

const byScheduledPublishDate = (a: ScheduledWorkout, b: ScheduledWorkout) => {
  if (!a.publishedAt && !b.publishedAt) return byProgramOrder(a, b);
  if (!a.publishedAt) return 1;
  if (!b.publishedAt) return -1;

  const diff =
    toScheduledMinute(a.publishedAt) - toScheduledMinute(b.publishedAt);
  return diff !== 0 ? diff : byProgramOrder(a, b);
};

type ScheduledWorkoutsVM = {
  isLoading: boolean;
  creator: { id: number };
  isEmpty: boolean;
  table: SmartTableProps<number>;
  search: WorkoutsSearchVM;
  pagination: WorkoutsPagination;
  convertToRestDay: ConvertToRestDayVM;
  duplicateDialog: DuplicateDialogProps | null;
};

export const useScheduledWorkouts = (
  influencerIdParam?: string,
): ScheduledWorkoutsVM => {
  const { influencerId, isLoading, creator, workouts, refetch, pagination } =
    useWorkoutsPageData({
      influencerIdParam,
      useQuery: useGetScheduledWorkoutsQuery,
      selectWorkouts: (data) => data?.creator?.scheduledWorkouts ?? [],
      sortPage: byScheduledPublishDate,
      fetchPolicy: 'network-only',
    });
  const { searchText, search } = useWorkoutsSearch();

  const liteWorkouts: LiteWorkout[] = workouts.map((w) =>
    toLiteWorkout(w, 'scheduled'),
  );

  const { duplicate, dialog: duplicateDialog } = useDuplicateWorkout(
    influencerId,
    (workoutId, targetProgramId) => {
      ajax.cloneWorkout(workoutId, targetProgramId).then(refetch);
    },
  );
  const duplicateById = (workoutId: number) => {
    const workout = liteWorkouts.find((w) => w.workoutId === workoutId);
    if (workout) duplicate(workout);
  };

  const convertFlow = useConvertToRestDay(refetch);
  const convert = (workoutId: number) =>
    convertFlow.openConfirmation(
      workoutId,
      liteWorkouts.find((w) => w.workoutId === workoutId)?.orgTitle ?? '',
    );

  const archive = (workoutId: number) => {
    if (confirm('Are you sure you want to *archive* this Workout?'))
      ajax.archiveWorkout(workoutId).then(refetch);
  };

  return {
    isLoading,
    creator,
    convertToRestDay: convertFlow.view,
    isEmpty: workouts.length === 0,
    table: {
      type: 'standard',
      value: buildWorkoutsListTable({
        kind: 'scheduled',
        influencerId,
        workouts: filterWorkoutsByTitle(liteWorkouts, searchText),
        programNameById: toProgramNameById(workouts),
        busyWorkoutIds: convertFlow.convertingWorkoutIds,
        actions: {
          onDuplicate: duplicateById,
          onArchive: archive,
          onConvertToRestDay: convert,
        },
      }),
    },
    search,
    pagination,
    duplicateDialog,
  };
};
